import StaticPageLayout from "@/components/layout/StaticPageLayout";
import { useLanguage } from "@/context/LanguageContext";

export default function PrivacyPolicy() {
  const { language } = useLanguage();

  const contentEn = (
    <div className="space-y-6 text-sm text-muted-foreground leading-relaxed">
      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">1. Introduction and Scope</h2>
        <p>
          Hamarsathi.com is committed to protecting the privacy and confidentiality of the personal information of our users. This Privacy Policy details how we collect, store, process, protect, and share your personal data when you register, access, or use our matrimonial services. By creating an account, you consent to the collection and use of your information as outlined in this policy.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">2. Personal Information We Collect</h2>
        <p>To provide a highly compatible and safe matchmaking service, we collect various types of information:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Profile Creation Information:</strong> Full name, gender, date of birth, religion, mother tongue, caste/sub-caste, native place, and current location.</li>
          <li><strong>Contact Details:</strong> Verified mobile number and email address.</li>
          <li><strong>Verification Documents:</strong> Government-issued ID cards (Aadhaar, Voter ID, PAN, Passport) for background and age check, and real-time selfie photographs.</li>
          <li><strong>Educational & Professional Details:</strong> Qualification, occupation, annual income, and employer name.</li>
          <li><strong>Family Details:</strong> Family type, values, parents' occupation, and siblings' information.</li>
          <li><strong>Technical Data:</strong> IP address, browser type, device details, and cookie data to improve website security and performance.</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">3. How We Use Your Information</h2>
        <p>We process your personal information for the following purposes:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>To display your profile to potential matches and run matchmaking algorithms.</li>
          <li>To verify profile authenticity and protect the community from fraud or impersonation.</li>
          <li>To facilitate communication through secure chat and contact detail exchange.</li>
          <li>To process premium membership payments securely.</li>
          <li>To send notifications, match updates, and alerts via email, SMS, and WhatsApp.</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">4. Data Sharing and Disclosure Policies</h2>
        <p>
          We do not sell, rent, or trade your personal information to third parties for marketing purposes. Your profile information is shared with other registered users on Hamarsathi.com to facilitate matrimonial matching. 
        </p>
        <p>
          <strong>Phone & Contact Sharing:</strong> Your verified phone number is kept confidential. It is only shared or made visible to another user when a mutual interest is explicitly accepted by both parties, or according to your custom privacy settings. We may disclose your information if required by law enforcement or to prevent illegal or harmful activity.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">5. Privacy Controls & Account Settings</h2>
        <p>We empower our members with complete control over their personal data:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Photo Privacy:</strong> You can set your photos to be visible to all, only to accepted matches, or hide them completely.</li>
          <li><strong>Phone Number Privacy:</strong> You can hide your number or only display it to matches who have accepted your interest.</li>
          <li><strong>Blocking & Reporting:</strong> You can permanently block members to prevent them from viewing your profile or sending messages.</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">6. Data Security Measures</h2>
        <p>
          We implement robust technical and organizational security measures, including SSL encryption, secure databases, and firewalls, to protect your information against unauthorized access, loss, or alteration. However, please note that no method of transmission over the Internet is 100% secure.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">7. Data Retention & Deactivation</h2>
        <p>
          We retain your personal information as long as your account is active. If you choose to delete or deactivate your account, we will purge or anonymize your profile data within 30 days, subject to legal compliance requirements (such as pending investigations or accounting audits).
        </p>
      </section>
    </div>
  );

  const contentHi = (
    <div className="space-y-6 text-sm text-muted-foreground leading-relaxed">
      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">1. प्रस्तावना और दायरा</h2>
        <p>
          हमरसाथी (Hamarsathi.com) हमारे उपयोगकर्ताओं की व्यक्तिगत जानकारी की गोपनीयता की रक्षा करने के लिए प्रतिबद्ध है। यह गोपनीयता नीति बताती है कि जब आप हमारी वैवाहिक सेवाओं का उपयोग करने के लिए पंजीकरण करते हैं, तो हम आपकी व्यक्तिगत जानकारी को कैसे एकत्र, संग्रहीत, संसाधित, सुरक्षित और साझा करते हैं। एक खाता बनाकर, आप इस नीति में उल्लिखित अपनी जानकारी के संग्रह और उपयोग के लिए सहमति देते हैं।
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">2. व्यक्तिगत जानकारी जो हम एकत्र करते हैं</h2>
        <p>सही जीवनसाथी की खोज को सुरक्षित और बेहतर बनाने के लिए हम विभिन्न प्रकार की जानकारी एकत्र करते हैं:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>प्रोफ़ाइल निर्माण जानकारी:</strong> पूरा नाम, लिंग, जन्म तिथि, धर्म, मातृभाषा, जाति/उप-जाति, मूल स्थान और वर्तमान स्थान।</li>
          <li><strong>संपर्क विवरण:</strong> सत्यापित मोबाइल नंबर और ईमेल पता।</li>
          <li><strong>सत्यापन दस्तावेज़:</strong> पृष्ठभूमि और आयु की जांच के लिए सरकारी पहचान पत्र (आधार, मतदाता पहचान पत्र, पैन, पासपोर्ट) और वास्तविक समय की सेल्फी फोटो।</li>
          <li><strong>शैक्षणिक और व्यावसायिक विवरण:</strong> योग्यता, व्यवसाय, वार्षिक आय और नियोक्ता का नाम।</li>
          <li><strong>पारिवारिक विवरण:</strong> परिवार का प्रकार, मूल्य, माता-पिता का व्यवसाय और भाई-बहनों की जानकारी।</li>
          <li><strong>तकनीकी डेटा:</strong> वेबसाइट की सुरक्षा और प्रदर्शन में सुधार के लिए आईपी पता, ब्राउज़र का प्रकार, डिवाइस विवरण और कुकी डेटा।</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">3. हम आपकी जानकारी का उपयोग कैसे करते हैं</h2>
        <p>हम आपकी व्यक्तिगत जानकारी को निम्नलिखित उद्देश्यों के लिए संसाधित करते हैं:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>अनुकूल जीवनसाथी के प्रोफाइल का सुझाव देने और मिलान एल्गोरिदम चलाने के लिए।</li>
          <li>प्रोफ़ाइल की सत्यता सत्यापित करने और धोखाधड़ी से समुदाय की रक्षा करने के लिए।</li>
          <li>सुरक्षित चैट और संपर्क विवरण साझा करने की सुविधा प्रदान करने के लिए।</li>
          <li>प्रीमियम सदस्यता भुगतानों को सुरक्षित रूप से संसाधित करने के लिए।</li>
          <li>ईमेल, एसएमएस और व्हाट्सएप के माध्यम से अलर्ट, मैच अपडेट और नोटिफिकेशन भेजने के लिए।</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">4. डेटा साझाकरण और प्रकटीकरण नीतियां</h2>
        <p>
          हम विपणन उद्देश्यों के लिए तीसरे पक्षों को आपकी व्यक्तिगत जानकारी बेचते या किराए पर नहीं देते हैं। वैवाहिक मिलान की सुविधा के लिए आपकी प्रोफ़ाइल जानकारी हमरसाथी पर अन्य पंजीकृत उपयोगकर्ताओं के साथ साझा की जाती है।
        </p>
        <p>
          <strong>फ़ोन और संपर्क साझाकरण:</strong> आपका सत्यापित फ़ोन नंबर पूरी तरह गोपनीय रखा जाता है। यह केवल तभी किसी अन्य उपयोगकर्ता को दिखाया जाता है जब दोनों पक्षों द्वारा आपसी रुचि स्वीकार कर ली जाती है। हम कानून प्रवर्तन या धोखाधड़ी की रोकथाम के लिए कानूनी रूप से आवश्यक होने पर जानकारी प्रकट कर सकते हैं।
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">5. गोपनीयता नियंत्रण और खाता सेटिंग्स</h2>
        <p>हम अपने सदस्यों को उनके व्यक्तिगत डेटा पर पूर्ण नियंत्रण प्रदान करते हैं:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>फोटो गोपनीयता:</strong> आप अपनी तस्वीरों को सभी को दिखाने, केवल स्वीकार किए गए मैचों को दिखाने या पूरी तरह से छिपाने के लिए सेट कर सकते हैं।</li>
          <li><strong>फ़ोन नंबर गोपनीयता:</strong> आप अपना नंबर छिपा सकते हैं या केवल उन्हीं मैचों को दिखा सकते हैं जिन्होंने आपकी रुचि स्वीकार की है।</li>
          <li><strong>ब्लॉकिंग और रिपोर्टिंग:</strong> आप अवांछित सदस्यों को स्थायी रूप से ब्लॉक कर सकते हैं ताकि वे आपकी प्रोफ़ाइल न देख सकें और न ही संदेश भेज सकें।</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">6. डेटा सुरक्षा उपाय</h2>
        <p>
          हम आपकी जानकारी को अनधिकृत पहुंच, हानि या परिवर्तन से बचाने के लिए एसएसएल एन्क्रिप्शन, सुरक्षित डेटाबेस और फ़ायरवॉल सहित मजबूत सुरक्षा उपाय लागू करते हैं। हालाँकि, इंटरनेट पर 100% सुरक्षा की गारंटी नहीं दी जा सकती।
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">7. डेटा प्रतिधारण और विलोपन</h2>
        <p>
          जब तक आपका खाता सक्रिय है, हम आपकी व्यक्तिगत जानकारी बनाए रखते हैं। यदि आप अपना खाता हटाने (Delete) का विकल्प चुनते हैं, तो हम 30 दिनों के भीतर आपकी प्रोफ़ाइल जानकारी को पूरी तरह से हटा देंगे, बशर्ते कोई कानूनी मामला या ऑडिट लंबित न हो।
        </p>
      </section>
    </div>
  );

  return (
    <StaticPageLayout
      title={language === "hi" ? "गोपनीयता नीति" : "Privacy Policy"}
      subtitle={language === "hi" ? "हम आपकी गोपनीयता और सुरक्षा का सम्मान करते हैं" : "We respect your privacy and security"}
    >
      {language === "hi" ? contentHi : contentEn}
    </StaticPageLayout>
  );
}
